/**
 * Transaction Table Component
 *
 * UI/UX ออกแบบสำหรับเว็บพนันออนไลน์
 * - สไตล์หรูหรา Premium
 * - สีสันชัดเจน อ่านง่าย
 * - ตัวเลขใหญ่ ใช้ font-mono
 */

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Eye,
  Check,
  X,
  RotateCcw,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Monitor,
  User,
  RefreshCw,
  Gift,
  QrCode,
  Clock,
  Copy,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { findBank } from "@/data/banks";
import { useTranslation } from "react-i18next";
import {
  TransactionProgress,
  TransactionProgressCompact,
} from "./transaction-progress";
import type { Transaction } from "@/domain/transaction/transaction.types";

// ============================================
// Types
// ============================================
// Note: Transaction interface is imported from transaction.types.ts

interface TransactionTableProps {
  transactions: Transaction[];
  isLoading?: boolean;
  activeFilterType?: string; // ✅ สำหรับแสดง TypeBadge ตาม filter (Deposit/Withdraw/All)
  hideColumns?: ("no" | "detail" | "member")[]; // ✅ ซ่อน columns ที่ไม่ต้องการ
  compactLayout?: boolean; // ✅ ใช้ layout แคบกว่า (สำหรับ Member Detail Dialog)
  onViewDetail?: (tx: Transaction) => void;
  onViewMember?: (memberId: string) => void;
  onApproveManual?: (id: string) => Promise<void>; // ✅ ใช้ onActionClick แทน แต่ยังเก็บไว้เพื่อความเข้ากันได้
  onApproveAuto?: (id: string) => Promise<void>;
  onResend?: (id: string) => Promise<void>;
  onReject?: (id: string) => Promise<void>;
  onCancel?: (id: string) => Promise<void>;
  onComplete?: (id: string) => Promise<void>;
  onActionClick?: (
    tx: Transaction,
    actionType: "cancel" | "reject" | "approve_manual" | "approve_auto"
  ) => void;
}

export type ActionType =
  | "approve_manual"
  | "approve_auto"
  | "resend"
  | "reject"
  | "cancel"
  | "complete";

// ============================================
// Helper Functions
// ============================================

/**
 * Get full URL for slip image
 * Runtime config takes precedence over build-time config
 */
function getSlipImageUrl(imagePath?: string): string | null {
  if (!imagePath) return null;

  // Normalize old format to new location
  // Old format: "uploads/1771420494750.jpg" → "uploads/slips-old/1771420494750.jpg"
  // New format: "uploads/slips/slip-2026-02-18-xxx.jpg" → keep as-is
  let normalizedPath = imagePath;

  if (normalizedPath.startsWith('uploads/') && !normalizedPath.includes('slips/')) {
    // Old format detected - map to slips-old directory
    normalizedPath = normalizedPath.replace('uploads/', 'uploads/slips-old/');
  }

  // Try runtime config first (from public/config.js)
  const runtimeConfig = (window as any).APP_CONFIG;
  if (runtimeConfig?.PARSE_V2_SERVER_URL) {
    const baseUrl = runtimeConfig.PARSE_V2_SERVER_URL.replace(/\/api\/v1$/, '');
    return `${baseUrl}/${normalizedPath}`;
  }

  // Fallback to build-time config
  const buildTimeUrl =
    import.meta.env.VITE_FRONT_API_URL ||
    import.meta.env.VITE_PARSE_V2_SERVER_URL?.replace(/\/api\/v[12]$/, '') ||
    '';
  return `${buildTimeUrl}/${normalizedPath}`;
}

// ============================================
// Helper Components
// ============================================

// ประเภท Badge - ใช้ theme colors (compact)
function TypeBadge({
  type,
  activeFilterType,
}: {
  type: string;
  activeFilterType?: string;
}) {
  // ✅ ถ้ามี activeFilterType → แสดงตาม filter
  if (activeFilterType === "Deposit") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-success text-success-foreground font-bold text-[10px] shadow-sm">
        <ArrowDownCircle className="w-3 h-3" />
        ฝาก
      </div>
    );
  }
  if (activeFilterType === "Withdraw") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-destructive text-destructive-foreground font-bold text-[10px] shadow-sm">
        <ArrowUpCircle className="w-3 h-3" />
        ถอน
      </div>
    );
  }

  // ✅ ถ้าไม่มี filter หรือ filter เป็น "" → แสดงตาม type จริง
  if (type === "Deposit") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-success text-success-foreground font-bold text-[10px] shadow-sm">
        <ArrowDownCircle className="w-3 h-3" />
        ฝาก
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-destructive text-destructive-foreground font-bold text-[10px] shadow-sm">
      <ArrowUpCircle className="w-3 h-3" />
      ถอน
    </div>
  );
}

// ช่องทาง Badge - ใช้ theme colors (compact)
function ChannelBadge({ channel }: { channel?: string | null }) {
  const config = {
    Panel: {
      icon: Monitor,
      label: "Panel",
      color: "bg-primary/10 text-primary border-primary/30",
    },
    Member: {
      icon: User,
      label: "Member",
      color: "bg-warning/10 text-warning border-warning/30",
    },
    Transfer: {
      icon: RefreshCw,
      label: "Transfer",
      color: "bg-muted text-muted-foreground border-border",
    },
  };
  const c = config[channel as keyof typeof config] || config.Panel;
  const Icon = c.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "inline-flex items-center justify-center w-6 h-6 rounded border",
              c.color
            )}
          >
            <Icon className="w-3.5 h-3.5" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{channel || "Panel"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// สถานะ Badge - ใช้ theme colors (compact) - รองรับ 5 สถานะ
function StatusBadge({ status }: { status: string }) {
  // ✅ Completed / Approved - สีเขียว
  if (status === "Approved" || status === "Completed") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-800 text-[10px] font-bold">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        สำเร็จ
      </div>
    );
  }
  // ✅ Pending - สีเหลือง
  if (status === "Pending") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-800 text-[10px] font-bold whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
        รอดำเนินการ
      </div>
    );
  }
  // ✅ Processing - สีน้ำเงิน
  if (status === "Processing") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-800 text-[10px] font-bold whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
        กำลังดำเนินการ
      </div>
    );
  }
  // ✅ Cancelled - สีเทา
  if (status === "Cancelled") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-950/30 text-gray-700 dark:text-gray-400 border border-gray-300 dark:border-gray-800 text-[10px] font-bold">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
        ยกเลิก
      </div>
    );
  }
  // ✅ Rejected - สีแดง
  if (status === "Rejected") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-800 text-[10px] font-bold">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        ปฏิเสธ
      </div>
    );
  }
  // Default (fallback)
  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/15 text-muted-foreground text-[10px] font-bold">
      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
      {status}
    </div>
  );
}

// รูปธนาคาร - Map จาก bank_name (ตัวเลข) กับ banklist (compact)
function BankImage({
  bankName,
  className,
}: {
  bankName?: string;
  className?: string;
}) {
  if (!bankName) return null;

  const bankInfo = findBank(bankName);

  if (!bankInfo) {
    return (
      <span className="text-[10px] text-muted-foreground">{bankName}</span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <img
            src={bankInfo.image}
            alt={bankInfo.name}
            className={cn(
              "w-5 h-5 rounded object-contain bg-white flex-shrink-0",
              className
            )}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </TooltipTrigger>
        <TooltipContent>
          <p>{bankInfo.name}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// จำนวนเงิน - ใช้ theme colors (compact)
function AmountDisplay({ amount, type }: { amount: number; type: string }) {
  const formatted = amount.toLocaleString("th-TH", {
    minimumFractionDigits: 2,
  });
  const isDeposit = type === "Deposit";

  return (
    <div
      className={cn(
        "font-mono text-sm font-bold tracking-tight",
        isDeposit ? "text-success" : "text-destructive"
      )}
    >
      {isDeposit ? "" : "-"}
      {formatted}
    </div>
  );
}

// ปุ่มคัดลอก
function CopyButton({ text, label }: { text: string; label?: string }) {
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation(); // ป้องกัน trigger parent click
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`คัดลอก${label ? ` ${label}` : ""}: ${text}`);
    } catch {
      toast.error("ไม่สามารถคัดลอกได้");
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-0.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
      title={`คัดลอก ${text}`}
    >
      <Copy className="w-3 h-3" />
    </button>
  );
}

// Action Buttons สำหรับ Withdraw - ใช้ theme colors
function WithdrawActions({
  tx,
  onAction,
}: {
  tx: Transaction;
  onAction: (type: ActionType) => void;
}) {
  // Parse ส่งมาเป็น number (0, 1, 2, 3) หรือ string ('0', '1', '2', '3') หรือ undefined
  // แปลงเป็น string ทั้งหมดเพื่อเปรียบเทียบ
  // ✅ รองรับทั้ง number และ string
  const credit = String(tx.isCreditWithdraw ?? 0);
  const cash = String(tx.isCashWithdraw ?? 0);
  // ✅ รองรับทั้ง "Manaul" (legacy typo จาก DB) และ "Manual" (ใหม่)
  const isManual = tx.withdrawBy === "Manaul" || tx.withdrawBy === "Manual";

  // เงื่อนไขตาม V2 - แยก Cancel และ Reject:
  // 1. credit='0', cash='0' (Pending) → Manual, Auto, ปฏิเสธ, ยกเลิก
  // 2. credit='1', cash='3' (Transfer Failed) → ส่งซ้ำ, เสร็จสิ้น, ยกเลิก
  // 3. credit='1', cash='2', withdrawBy='Manual' (Processing Manual) → ยกเลิก, เสร็จสิ้น
  // 4. credit='1', cash='2', withdrawBy='Auto' (Processing Auto) → ยกเลิก
  const showApprove = credit === "0" && cash === "0";
  const showResendComplete = credit === "1" && cash === "3";
  const showManualComplete = credit === "1" && cash === "2" && isManual;
  const showAutoProcessing = credit === "1" && cash === "2" && !isManual; // Auto Processing

  // Debug log (เฉพาะ development)
  if (process.env.NODE_ENV === "development" && tx.type === "Withdraw") {
    console.log("[WithdrawActions] Debug:", {
      id: tx.id,
      credit,
      cash,
      withdrawBy: tx.withdrawBy,
      isManual,
      showApprove,
      showResendComplete,
      showManualComplete,
      showAutoProcessing,
      status: tx.status,
    });
  }

  if (
    !showApprove &&
    !showResendComplete &&
    !showManualComplete &&
    !showAutoProcessing
  )
    return null;

  return (
    <div className="flex flex-wrap gap-0.5">
      {/* Case 1: Pending (credit='0', cash='0') → อนุมัติ Manual/Auto, ปฏิเสธ, ยกเลิก */}
      {showApprove && (
        <>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-success hover:bg-success/90 text-success-foreground"
            onClick={() => onAction("approve_manual")}
          >
            <Check className="w-2.5 h-2.5 mr-0.5" />
            Manual
          </Button>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-warning hover:bg-warning/90 text-warning-foreground"
            onClick={() => onAction("approve_auto")}
          >
            <Check className="w-2.5 h-2.5 mr-0.5" />
            Auto
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="h-6 px-1.5 text-[9px] bg-red-600 hover:bg-red-700"
            onClick={() => onAction("reject")}
          >
            <X className="w-2.5 h-2.5 mr-0.5" />
            ปฏิเสธ
          </Button>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-gray-600 hover:bg-gray-700 text-white"
            onClick={() => onAction("cancel")}
          >
            <X className="w-2.5 h-2.5 mr-0.5" />
            ยกเลิก
          </Button>
        </>
      )}

      {/* Case 2: Transfer Failed (credit='1', cash='3') → ส่งซ้ำ/เสร็จสิ้น/ยกเลิก */}
      {showResendComplete && (
        <>
          <Button
            size="sm"
            variant="secondary"
            className="h-6 px-1.5 text-[9px]"
            onClick={() => onAction("resend")}
          >
            <RotateCcw className="w-2.5 h-2.5 mr-0.5" />
            ส่งซ้ำ
          </Button>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-success hover:bg-success/90 text-success-foreground"
            onClick={() => onAction("complete")}
          >
            <Check className="w-2.5 h-2.5 mr-0.5" />
            เสร็จ
          </Button>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-gray-600 hover:bg-gray-700 text-white"
            onClick={() => onAction("cancel")}
          >
            <X className="w-2.5 h-2.5 mr-0.5" />
            ยกเลิก
          </Button>
        </>
      )}

      {/* Case 3: Processing Manual (credit='1', cash='2', Manual) → ยกเลิก/เสร็จสิ้น */}
      {showManualComplete && (
        <>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-gray-600 hover:bg-gray-700 text-white"
            onClick={() => onAction("cancel")}
          >
            <X className="w-2.5 h-2.5 mr-0.5" />
            ยกเลิก
          </Button>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-success hover:bg-success/90 text-success-foreground"
            onClick={() => onAction("complete")}
          >
            <Check className="w-2.5 h-2.5 mr-0.5" />
            เสร็จสิ้น
          </Button>
        </>
      )}

      {/* Case 4: Processing Auto (credit='1', cash='2', Auto) → ยกเลิก (คืนเครดิต) */}
      {showAutoProcessing && (
        <>
          <Button
            size="sm"
            className="h-6 px-1.5 text-[9px] bg-gray-600 hover:bg-gray-700 text-white"
            onClick={() => onAction("cancel")}
          >
            <X className="w-2.5 h-2.5 mr-0.5" />
            ยกเลิก
          </Button>
        </>
      )}
    </div>
  );
}

// Withdraw Status Display - ใช้ theme colors (compact)
function WithdrawStatus({ tx }: { tx: Transaction }) {
  const credit = String(tx.isCreditWithdraw ?? 0);
  const cash = String(tx.isCashWithdraw ?? 0);
  const isManual = tx.withdrawBy === "Manaul" || tx.withdrawBy === "Manual";

  const creditLabels: Record<string, { label: string; color: string }> = {
    "0": { label: "รอตัดเครดิต", color: "text-muted-foreground" },
    "1": { label: "ตัดเครดิต✓", color: "text-success" },
    "2": { label: "กำลังตัด...", color: "text-warning" },
    "3": { label: "ตัดไม่สำเร็จ", color: "text-destructive" },
  };

  const cashLabels: Record<string, { label: string; color: string }> = {
    "0": {
      label: `รอถอน${isManual ? "มือ" : ""}`,
      color: "text-muted-foreground",
    },
    "1": { label: `ถอน✓`, color: "text-success" },
    "2": { label: `กำลังถอน...`, color: "text-warning" },
    "3": {
      label: `ถอน${isManual ? "มือ" : "ออโต้"}ไม่สำเร็จ`,
      color: "text-destructive",
    },
  };

  const creditInfo = creditLabels[credit] || creditLabels["0"];
  const cashInfo = cashLabels[cash] || cashLabels["0"];

  return (
    <div className="space-y-0.5 text-[10px]">
      <div className={cn("flex items-center gap-1", creditInfo.color)}>
        <span className="w-1 h-1 rounded-full bg-current flex-shrink-0" />
        <span className="truncate">{creditInfo.label}</span>
        {credit === "1" && tx.isCreditWithdrawAmount && (
          <span className="font-mono text-success text-[9px]">
            {tx.isCreditWithdrawAmount.toLocaleString()}
          </span>
        )}
      </div>
      {!tx.isBonus && (
        <div className={cn("flex items-center gap-1", cashInfo.color)}>
          <span className="w-1 h-1 rounded-full bg-current flex-shrink-0" />
          <span className="truncate">{cashInfo.label}</span>
          {cash === "1" && tx.isCashWithdrawAmount && (
            <span className="font-mono text-success text-[9px]">
              {tx.isCashWithdrawAmount.toLocaleString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function TransactionTable({
  transactions,
  isLoading,
  activeFilterType, // ✅ เพิ่ม prop
  hideColumns = [], // ✅ ซ่อน columns ที่ไม่ต้องการ
  compactLayout = false, // ✅ Default: false (ใช้ layout ปกติ)
  onViewDetail,
  onViewMember,
  onApproveManual: _onApproveManual, // ✅ ใช้ onActionClick แทน แต่ยังเก็บไว้เพื่อความเข้ากันได้
  onApproveAuto: _onApproveAuto, // ✅ ใช้ onActionClick แทน แต่ยังเก็บไว้เพื่อความเข้ากันได้
  onResend,
  onReject,
  onComplete,
  onActionClick,
}: TransactionTableProps) {
  const hideNo = hideColumns.includes("no");
  const hideDetail = hideColumns.includes("detail");
  const hideMember = hideColumns.includes("member");
  const { t } = useTranslation();
  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    action: (() => Promise<void>) | null;
  }>({ open: false, title: "", description: "", action: null });
  const [isProcessing, setIsProcessing] = useState(false);

  // Expanded rows state - เก็บ id ของแถวที่ expand อยู่
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isExpanded = (id: string) => expandedRows.has(id);

  const handleAction = (tx: Transaction, actionType: ActionType) => {
    // cancel และ reject จะใช้ ReasonDialog แทน (ไม่ใช้ confirmDialog)
    if (actionType === "cancel" || actionType === "reject") {
      // ส่งไปให้ parent component จัดการ
      if (onActionClick) {
        onActionClick(tx, actionType);
      }
      return;
    }

    // ✅ สำหรับ approve_manual และ approve_auto → เปิด ApproveBankDialog โดยตรง (ไม่แสดง confirm dialog)
    // ให้ ApproveBankDialog แสดง confirm dialog หลังจากเลือกบัญชีแล้ว
    if (actionType === "approve_manual" || actionType === "approve_auto") {
      // ส่งไปให้ parent component จัดการ (เปิด ApproveBankDialog)
      if (onActionClick) {
        onActionClick(tx, actionType);
      }
      return;
    }

    const configs: Partial<
      Record<
        ActionType,
        {
          title: string;
          desc: string;
          fn: ((id: string) => Promise<void>) | undefined;
        }
      >
    > = {
      resend: {
        title: "ยืนยันส่งซ้ำ",
        desc: "ต้องการส่งรายการนี้ซ้ำ?",
        fn: onResend,
      },
      reject: {
        title: "ยืนยันปฏิเสธ",
        desc: "ต้องการปฏิเสธรายการนี้?",
        fn: onReject,
      },
      cancel: {
        title: "ยืนยันยกเลิก",
        desc: "ต้องการยกเลิกรายการนี้?",
        fn: undefined, // จะใช้ ReasonDialog แทน
      },
      complete: {
        title: "ยืนยันเสร็จสิ้น",
        desc: "ต้องการทำรายการนี้เสร็จสิ้น?",
        fn: onComplete,
      },
    };

    const config = configs[actionType];
    if (!config || !config.fn) return;

    setConfirmDialog({
      open: true,
      title: config.title,
      description: config.desc,
      action: () => config.fn!(tx.id),
    });
  };

  const executeAction = async () => {
    if (!confirmDialog.action) return;
    setIsProcessing(true);
    try {
      await confirmDialog.action();
    } finally {
      setIsProcessing(false);
      setConfirmDialog({
        open: false,
        title: "",
        description: "",
        action: null,
      });
    }
  };

  // คำนวณเวลาที่รอ
  const getElapsedTime = (dateStr?: string) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;

    if (diffHours > 0) {
      return {
        text: `${diffHours}ชม. ${mins}น.`,
        isLong: diffMins > 30,
        isVeryLong: diffMins > 60,
      };
    }
    return {
      text: `${diffMins} นาที`,
      isLong: diffMins > 5,
      isVeryLong: diffMins > 15,
    };
  };

  // Format date - เวลาใหญ่ วันเล็ก
  const formatDate = (dateStr?: string, showElapsed?: boolean) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const time = d.toLocaleTimeString("th-TH", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const date = d.toLocaleDateString("th-TH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
    const elapsed = showElapsed ? getElapsedTime(dateStr) : null;

    return (
      <div className="space-y-0.5">
        {/* Elapsed time badge สำหรับ pending */}
        {elapsed && (
          <div
            className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold",
              elapsed.isVeryLong
                ? "bg-red-500 text-white"
                : elapsed.isLong
                ? "bg-amber-500 text-white"
                : "bg-emerald-500 text-white"
            )}
          >
            <Clock className="w-2.5 h-2.5" />
            {elapsed.text}
          </div>
        )}
        {/* เวลา - ใหญ่กว่า */}
        <div className="text-sm font-bold font-mono">{time}</div>
        {/* วัน - เล็กกว่า */}
        <div className="text-[10px] text-muted-foreground">{date}</div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Empty state
  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-muted-foreground/30 mb-4" />
        <p className="text-lg font-medium">ไม่พบรายการ</p>
        <p className="text-sm text-muted-foreground">
          ลองเปลี่ยนเงื่อนไขการค้นหา
        </p>
      </div>
    );
  }

  // Column widths - ใช้ % เพื่อให้ responsive และตรงกันทุกตาราง
  // ลำดับ: no, ดู, เวลา, ช่องทาง, ประเภท, สมาชิก, บัญชีลูกค้า, บัญชีเว็บ, จำนวนเงิน, สถานะ, ผู้ดำเนินการ, รายละเอียด
  const colWidths = compactLayout
    ? {
        // ✅ Compact layout สำหรับ Member Detail Dialog
        no: "w-[5%] min-w-[80px]",
        view: "w-[3%] min-w-[28px]",
        time: "w-[6%] min-w-[65px]",
        channel: "w-[2.5%] min-w-[26px]",
        type: "w-[4.5%] min-w-[50px]",
        member: "w-[9%] min-w-[90px]",
        account: "w-[8%] min-w-[80px]", // บัญชีลูกค้า
        webAccount: "w-[8%] min-w-[80px]", // บัญชีเว็บ
        amount: "w-[5%] min-w-[55px]",
        status: "w-[10%] min-w-[100px]", // สถานะ
        operator: "w-[8%] min-w-[90px]", // ผู้ดำเนินการ
        detail: "w-[27%] min-w-[180px]",
      }
    : {
        // ✅ Normal layout สำหรับ Smart Cashier และหน้าอื่นๆ
        no: "w-[5%] min-w-[90px]",
        view: "w-[3%] min-w-[28px]",
        time: "w-[6%] min-w-[65px]",
        channel: "w-[3%] min-w-[32px]",
        type: "w-[4.5%] min-w-[50px]",
        member: "w-[9%] min-w-[95px]",
        account: "w-[9%] min-w-[90px]", // บัญชีลูกค้า
        webAccount: "w-[9%] min-w-[90px]", // บัญชีเว็บ
        amount: "w-[6.5%] min-w-[70px]", // จำนวนเงิน
        status: "w-[9%] min-w-[85px]", // สถานะ
        operator: "w-[8%] min-w-[90px]", // ผู้ดำเนินการ
        detail: "w-[23%] min-w-[180px]",
      };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed min-w-[950px]">
          <thead>
            <tr className="border-b border-border/50 bg-muted/30">
              {!hideNo && (
                <th
                  className={cn(
                    "px-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                    colWidths.no
                  )}
                >
                  {t("smartCashier.transactionId")}
                </th>
              )}
              <th
                className={cn(
                  "px-1 py-2 text-center text-xs font-semibold text-muted-foreground",
                  colWidths.view
                )}
              ></th>
              <th
                className={cn(
                  "pl-2 pr-1 py-2 text-left text-xs font-semibold text-muted-foreground",
                  colWidths.time
                )}
              >
                {t("smartCashier.time")}
              </th>
              <th
                className={cn(
                  "pl-1 pr-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                  colWidths.channel
                )}
              >
                {t("smartCashier.channel")}
              </th>
              <th
                className={cn(
                  "px-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                  colWidths.type
                )}
              >
                {t("smartCashier.type")}
              </th>
              {!hideMember && (
                <th
                  className={cn(
                    "px-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                    colWidths.member
                  )}
                >
                  {t("smartCashier.member")}
                </th>
              )}
              <th
                className={cn(
                  "px-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                  colWidths.account
                )}
              >
                บัญชีลูกค้า
              </th>
              <th
                className={cn(
                  "pl-1 pr-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                  colWidths.webAccount
                )}
              >
                บัญชีเว็บ
              </th>
              <th
                className={cn(
                  "pl-0 pr-1 py-2 text-right text-xs font-semibold text-muted-foreground",
                  colWidths.amount
                )}
              >
                {t("smartCashier.amount")}
              </th>
              {!hideDetail && (
                <th
                  className={cn(
                    "px-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                    colWidths.detail
                  )}
                >
                  {t("smartCashier.detail")}
                </th>
              )}
              {/* สถานะ */}
              <th
                className={cn(
                  "pl-1 pr-2 py-2 text-center text-xs font-semibold text-muted-foreground",
                  colWidths.status
                )}
              >
                {t("smartCashier.status")}
              </th>
              {/* ผู้ดำเนินการ */}
              <th
                className={cn(
                  "px-2 py-2 text-left text-xs font-semibold text-muted-foreground",
                  colWidths.operator
                )}
              >
                ผู้ดำเนินการ
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/30">
            {transactions.map((tx) => {
              const txExpanded = isExpanded(tx.id);
              const showExpandButton =
                tx.type === "Withdraw" ||
                tx.status === "Processing" ||
                tx.statusNote ||
                tx.apiBankObj?.notes;

              return (
                <React.Fragment key={tx.id}>
                  <tr
                    className={cn(
                      "hover:bg-muted/20 transition-colors group",
                      txExpanded && "bg-muted/10"
                    )}
                  >
                    {/* ✅ เลขที่ (Transfer ID) - แสดงแบบ truncate + icon คัดลอก (ตัดให้เท่ากับตัวสั้น) */}
                    {!hideNo && (
                      <td className={cn("px-2 py-2 align-top", colWidths.no)}>
                        {tx.transferId ? (
                          <div className="flex items-start gap-1 min-w-0">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs font-medium text-primary truncate block w-[70px] leading-5">
                                    {tx.transferId}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{tx.transferId}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <div className="pt-0.5">
                              <CopyButton text={tx.transferId} label="เลขที่" />
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            -
                          </span>
                        )}
                      </td>
                    )}

                    {/* View/Expand - ปุ่มดูและ expand (align-top เพื่อให้แนวเดียวกับเลขที่) */}
                    <td
                      className={cn(
                        "px-1 py-2 text-center align-top",
                        colWidths.view
                      )}
                    >
                      <div className="flex items-center gap-0.5">
                        {showExpandButton && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-60 hover:opacity-100"
                            onClick={() => toggleExpand(tx.id)}
                          >
                            {txExpanded ? (
                              <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-50 group-hover:opacity-100"
                          onClick={() => onViewDetail?.(tx)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>

                    {/* เวลา - แสดง elapsed time ถ้าเป็น Pending (ชิดช่องทาง) */}
                    <td
                      className={cn(
                        "pl-1 pr-0.5 py-2 align-top",
                        colWidths.time
                      )}
                    >
                      {formatDate(
                        tx.type === "Withdraw"
                          ? tx.dateTime
                          : tx.datetimeCreated || tx.createdAt,
                        tx.status === "Pending" // showElapsed สำหรับรายการรอดำเนินการ
                      )}
                    </td>

                    {/* ช่องทาง - ชิดเวลา */}
                    <td
                      className={cn(
                        "pl-0.5 pr-1 py-2 align-top",
                        colWidths.channel
                      )}
                    >
                      <ChannelBadge channel={tx.createFrom} />
                    </td>

                    {/* ประเภท - ชิดช่องทาง */}
                    <td className={cn("px-1 py-2", colWidths.type)}>
                      <TypeBadge
                        type={tx.type}
                        activeFilterType={activeFilterType}
                      />
                    </td>

                    {/* สมาชิก + ปุ่มคัดลอก */}
                    {!hideMember && (
                      <td className={cn("px-2 py-2", colWidths.member)}>
                        {tx.createFrom === "Transfer" ? (
                          <span className="text-muted-foreground text-sm">
                            -
                          </span>
                        ) : (
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <button
                                className="text-xs font-medium text-primary hover:underline flex items-center gap-1 truncate"
                                onClick={() =>
                                  tx.memberId && onViewMember?.(tx.memberId)
                                }
                              >
                                <span className="truncate">
                                  {tx.memberUsername || "-"}
                                </span>
                                {tx.memberAgentStatus === "ACTIVE" && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-success flex-shrink-0" />
                                )}
                                {tx.memberAgentStatus === "SUSPEND" && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-destructive flex-shrink-0" />
                                )}
                              </button>
                              {tx.memberUsername && (
                                <CopyButton
                                  text={tx.memberUsername}
                                  label="ยูสเซอร์"
                                />
                              )}
                            </div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {tx.memberName}
                            </div>
                          </div>
                        )}
                      </td>
                    )}

                    {/* บัญชี + ปุ่มคัดลอก */}
                    <td className={cn("px-2 py-2", colWidths.account)}>
                      {tx.isBonus ? (
                        <div className="flex items-center gap-1">
                          <Gift className="w-3.5 h-3.5 text-warning flex-shrink-0" />
                          <span className="text-[10px] font-medium text-warning">
                            โบนัส
                          </span>
                        </div>
                      ) : tx.createFrom === "Transfer" ? (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <BankImage bankName={tx.transferToBankName} />
                          <div className="min-w-0 flex items-center gap-1">
                            <div className="text-xs font-medium font-mono truncate">
                              {tx.transferToBankAccount || "-"}
                            </div>
                            {tx.transferToBankAccount && (
                              <CopyButton
                                text={tx.transferToBankAccount}
                                label="เลขบัญชี"
                              />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <BankImage bankName={tx.memberBankName} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <div className="text-xs font-medium font-mono truncate">
                                {tx.memberBankAccount || "-"}
                              </div>
                              {tx.memberBankAccount && (
                                <CopyButton
                                  text={tx.memberBankAccount}
                                  label="เลขบัญชี"
                                />
                              )}
                            </div>
                            {tx.memberName && (
                              <div className="text-[10px] text-muted-foreground truncate">
                                {tx.memberName}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>

                    {/* บัญชีเว็บ */}
                    <td className={cn("pl-1 pr-1 py-2", colWidths.webAccount)}>
                      {tx.webBankName || tx.webBankAccount ? (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <BankImage bankName={tx.webBankName} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <div className="text-xs font-medium font-mono truncate">
                                {tx.webBankAccount || "-"}
                              </div>
                              {tx.webBankAccount && (
                                <CopyButton
                                  text={tx.webBankAccount}
                                  label="เลขบัญชีเว็บ"
                                />
                              )}
                            </div>
                            {tx.webBankAccountName && (
                              <div className="text-[10px] text-muted-foreground truncate">
                                {tx.webBankAccountName}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>

                    {/* จำนวนเงิน */}
                    <td
                      className={cn(
                        "pl-0 pr-1 py-2 text-right",
                        colWidths.amount
                      )}
                    >
                      <AmountDisplay amount={tx.amount} type={tx.type} />
                    </td>

                    {/* รายละเอียด */}
                    {!hideDetail && (
                      <td className={cn("px-2 py-2", colWidths.detail)}>
                        <div className="space-y-1">
                          {/* Withdraw Status - แสดงสำหรับ Pending และ Processing */}
                          {tx.type === "Withdraw" &&
                            (tx.status === "Pending" ||
                              tx.status === "Processing") && (
                              <WithdrawStatus tx={tx} />
                            )}

                          {/* Action Buttons - Withdraw (Pending และ Processing) */}
                          {(tx.status === "Pending" ||
                            tx.status === "Processing") &&
                            tx.type === "Withdraw" && (
                              <WithdrawActions
                                tx={tx}
                                onAction={(type) => handleAction(tx, type)}
                              />
                            )}

                          {/* Deposit Actions */}
                          {tx.status === "Pending" && tx.type === "Deposit" && (
                            <div className="flex gap-0.5">
                              <Button
                                size="sm"
                                className="h-6 px-1.5 text-[9px] bg-success hover:bg-success/90 text-success-foreground"
                                onClick={() =>
                                  handleAction(tx, "approve_manual")
                                }
                              >
                                <Check className="w-2.5 h-2.5 mr-0.5" />
                                อนุมัติ
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-6 px-1.5 text-[9px] bg-red-600 hover:bg-red-700"
                                onClick={() => handleAction(tx, "reject")}
                              >
                                <X className="w-2.5 h-2.5 mr-0.5" />
                                ปฏิเสธ
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-1.5 text-[9px] border-gray-300 hover:bg-gray-100"
                                onClick={() => handleAction(tx, "cancel")}
                              >
                                <RotateCcw className="w-2.5 h-2.5 mr-0.5" />
                                ยกเลิก
                              </Button>
                            </div>
                          )}

                          {/* Notes */}
                          {tx.notes && (
                            <p
                              className="text-[10px] text-muted-foreground truncate"
                              title={tx.notes}
                            >
                              📝 {tx.notes}
                            </p>
                          )}

                          {/* Status Note */}
                          {tx.statusNote && (
                            <p
                              className={cn(
                                "text-[10px] font-medium truncate",
                                tx.statusNote.includes("ไม่พบ") &&
                                  "text-destructive",
                                tx.statusNote.includes("ซ้ำ") &&
                                  "text-destructive",
                                tx.statusNote.includes("รอ") && "text-warning",
                                tx.statusNote.includes("ดีเลย์") &&
                                  "text-muted-foreground"
                              )}
                              title={tx.statusNote}
                            >
                              {tx.statusNote}
                            </p>
                          )}

                          {/* QR Code Link */}
                          {tx.apiBankObj &&
                            "imagePath" in tx.apiBankObj &&
                            tx.apiBankObj.imagePath && (
                              <a
                                href={getSlipImageUrl(tx.apiBankObj.imagePath) || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline"
                              >
                                <QrCode className="w-2.5 h-2.5" />
                                สลิป
                              </a>
                            )}

                          {/* UUID (สำหรับ payment gateway) - แสดงในรายละเอียด */}
                          {tx.uuid && (
                            <p
                              className="text-[9px] font-mono text-muted-foreground truncate"
                              title={`Payment Gateway UUID: ${tx.uuid}`}
                            >
                              UUID: {tx.uuid.slice(0, 16)}...
                            </p>
                          )}

                          {/* ✅ Promotion Info - แสดงชัดเจนสำหรับ Manual Withdraw */}
                          {tx.promotionInfo && (
                            <div className="mt-1 p-2 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                              <div className="flex items-center gap-1 mb-2">
                                <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400" />
                                <span className="text-[10px] font-semibold text-yellow-800 dark:text-yellow-300">
                                  ⚠️ ลูกค้าติดโปรโมชัน: {tx.promotionInfo.code}
                                </span>
                              </div>
                              <div className="text-[9px] text-yellow-700 dark:text-yellow-400 space-y-1">
                                <div className="flex justify-between">
                                  <span>ตัดเครดิต:</span>
                                  <span className="font-mono font-semibold">
                                    {tx.promotionInfo.amount_credit?.toLocaleString()}{" "}
                                    บาท
                                  </span>
                                </div>
                                <div className="flex justify-between pt-1 border-t border-yellow-300 dark:border-yellow-700">
                                  <span className="font-semibold">
                                    💰 เงินที่ต้องโอนให้ลูกค้า:
                                  </span>
                                  <span className="font-mono font-bold text-yellow-900 dark:text-yellow-200 text-[10px]">
                                    {tx.promotionInfo.amount_cash?.toLocaleString()}{" "}
                                    บาท
                                  </span>
                                </div>
                                {tx.promotionInfo.max_withdraw && (
                                  <div className="text-[8px] text-yellow-600 dark:text-yellow-500 pt-0.5">
                                    (เงื่อนไข: ถอนได้ไม่เกิน{" "}
                                    {tx.promotionInfo.max_withdraw.toLocaleString()}{" "}
                                    บาท)
                                  </div>
                                )}
                              </div>
                            </div>
                          )}


                          {/* Compact Progress Indicator - แสดงเสมอสำหรับ Withdraw ที่กำลังดำเนินการ */}
                          {tx.type === "Withdraw" &&
                            (tx.status === "Processing" ||
                              tx.status === "Pending") &&
                            !txExpanded && (
                              <div className="mt-1">
                                <TransactionProgressCompact
                                  type={tx.type}
                                  status={tx.status}
                                  isCreditWithdraw={tx.isCreditWithdraw}
                                  isCashWithdraw={tx.isCashWithdraw}
                                  withdrawBy={tx.withdrawBy}
                                />
                              </div>
                            )}
                        </div>
                      </td>
                    )}

                    {/* สถานะ */}
                    <td
                      className={cn(
                        "pl-1 pr-2 py-2 text-center",
                        colWidths.status
                      )}
                    >
                      <StatusBadge status={tx.status} />
                    </td>

                    {/* ผู้ดำเนินการ */}
                    <td className={cn("px-2 py-2", colWidths.operator)}>
                      <div className="min-w-0">
                        {tx.updatedBy ? (
                          <div className="space-y-0.5">
                            <div className="text-xs font-medium text-foreground truncate">
                              {tx.updatedBy}
                            </div>
                            {tx.createdBy && tx.createdBy !== tx.updatedBy && (
                              <div className="text-[10px] text-muted-foreground truncate">
                                สร้าง: {tx.createdBy}
                              </div>
                            )}
                          </div>
                        ) : tx.createdBy ? (
                          <div className="text-xs font-medium text-muted-foreground truncate">
                            {tx.createdBy}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row - แสดงรายละเอียดเพิ่มเติม */}
                  {txExpanded && showExpandButton && (
                    <tr className="bg-muted/5">
                      <td
                        colSpan={
                          (hideNo ? 0 : 1) +
                          1 + // view
                          1 + // time
                          1 + // channel
                          1 + // type
                          (hideMember ? 0 : 1) + // member
                          1 + // account
                          1 + // webAccount
                          1 + // amount
                          (hideDetail ? 0 : 1) + // detail
                          1 + // status
                          1 // operator
                        }
                        className="px-4 py-3"
                      >
                        <div className="max-w-3xl mx-auto">
                          <TransactionProgress
                            type={tx.type}
                            status={tx.status}
                            isCreditWithdraw={tx.isCreditWithdraw}
                            isCashWithdraw={tx.isCashWithdraw}
                            withdrawBy={tx.withdrawBy}
                            isCreditWithdrawAmount={tx.isCreditWithdrawAmount}
                            isCashWithdrawAmount={tx.isCashWithdrawAmount}
                            statusNote={tx.statusNote}
                            apiBankObj={tx.apiBankObj}
                            promotionInfo={tx.promotionInfo}
                            createdBy={tx.createdBy}
                            updatedBy={tx.updatedBy}
                            createdAt={tx.createdAt}
                            updatedAt={tx.updatedAt}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          !open && setConfirmDialog({ ...confirmDialog, open: false })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>
              ยกเลิก
            </AlertDialogCancel>
            <AlertDialogAction onClick={executeAction} disabled={isProcessing}>
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              ตกลง
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
